import React, { useMemo, useState } from 'react';
import { Calendar as CalendarIcon, Clock, Settings, Plus, ChevronLeft, ChevronRight, Trash2, Copy, Video, Phone, MessageCircle, Play, Mail, CheckCircle2, X, AlertCircle } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Input } from '../ui/Input';
interface CalendarScheduleTabProps {
  showToast: (msg: string) => void;
}
type ViewType = 'calendar' | 'settings';
type SlotType = 'video' | 'audio' | 'chat';
interface Slot {
  id: string;
  dateStr: string; // YYYY-MM-DD
  startTime: string; // HH:mm
  endTime: string; // HH:mm
  type: SlotType;
  isBooked: boolean;
  bookingId?: string;
}
interface Booking {
  id: string;
  dateStr: string;
  startTime: string;
  endTime: string;
  clientName: string;
  serviceName: string;
  type: SlotType;
  paymentStatus: 'paid' | 'pending';
}
const MONTH_NAMES = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
const DAY_NAMES = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const formatDate = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};
export function CalendarScheduleTab({
  showToast
}: CalendarScheduleTabProps) {
  const [view, setView] = useState<ViewType>('calendar');
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [showAddSlot, setShowAddSlot] = useState(false);
  const [newSlot, setNewSlot] = useState({
    startTime: '10:00',
    endTime: '11:00',
    type: 'video' as SlotType
  });
  // Mock Data
  const [slots, setSlots] = useState<Slot[]>(() => {
    const initialSlots: Slot[] = [];
    const today = new Date();
    const todayStr = formatDate(today);
    initialSlots.push({
      id: 's1',
      dateStr: todayStr,
      startTime: '10:00',
      endTime: '11:00',
      type: 'video',
      isBooked: false
    });
    initialSlots.push({
      id: 's2',
      dateStr: todayStr,
      startTime: '11:30',
      endTime: '12:30',
      type: 'audio',
      isBooked: false
    });
    initialSlots.push({
      id: 's3',
      dateStr: todayStr,
      startTime: '14:00',
      endTime: '15:00',
      type: 'video',
      isBooked: true,
      bookingId: 'b1'
    });
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = formatDate(tomorrow);
    initialSlots.push({
      id: 's4',
      dateStr: tomorrowStr,
      startTime: '09:00',
      endTime: '10:00',
      type: 'chat',
      isBooked: false
    });
    initialSlots.push({
      id: 's5',
      dateStr: tomorrowStr,
      startTime: '16:30',
      endTime: '17:15',
      type: 'audio',
      isBooked: true,
      bookingId: 'b2'
    });
    return initialSlots;
  });
  const [bookings, setBookings] = useState<Booking[]>(() => {
    const today = new Date();
    const todayStr = formatDate(today);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = formatDate(tomorrow);
    return [{
      id: 'b1',
      dateStr: todayStr,
      startTime: '14:00',
      endTime: '15:00',
      clientName: 'Елена С.',
      serviceName: 'Натальная карта',
      type: 'video',
      paymentStatus: 'paid'
    }, {
      id: 'b2',
      dateStr: tomorrowStr,
      startTime: '16:30',
      endTime: '17:15',
      clientName: 'Дмитрий В.',
      serviceName: 'Соляр',
      type: 'audio',
      paymentStatus: 'paid'
    }];
  });
  const [settings, setSettings] = useState({
    slotDuration: '60',
    breakDuration: '15',
    timezone: 'Europe/Moscow',
    workingDays: [{
      day: 'Понедельник',
      active: true,
      start: '10:00',
      end: '19:00'
    }, {
      day: 'Вторник',
      active: true,
      start: '10:00',
      end: '19:00'
    }, {
      day: 'Среда',
      active: true,
      start: '10:00',
      end: '19:00'
    }, {
      day: 'Четверг',
      active: true,
      start: '10:00',
      end: '19:00'
    }, {
      day: 'Пятница',
      active: true,
      start: '10:00',
      end: '18:00'
    }, {
      day: 'Суббота',
      active: false,
      start: '10:00',
      end: '15:00'
    }, {
      day: 'Воскресенье',
      active: false,
      start: '10:00',
      end: '15:00'
    }]
  });
  const calendarGrid = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    let startDayOfWeek = firstDay.getDay() - 1;
    if (startDayOfWeek === -1) startDayOfWeek = 6;
    const daysInMonth = lastDay.getDate();
    const grid: {
      date: Date;
      isCurrentMonth: boolean;
      dateStr: string;
    }[] = [];
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      const d = new Date(year, month - 1, prevMonthLastDay - i);
      grid.push({
        date: d,
        isCurrentMonth: false,
        dateStr: formatDate(d)
      });
    }
    for (let i = 1; i <= daysInMonth; i++) {
      const d = new Date(year, month, i);
      grid.push({
        date: d,
        isCurrentMonth: true,
        dateStr: formatDate(d)
      });
    }
    const remainingCells = 42 - grid.length;
    for (let i = 1; i <= remainingCells; i++) {
      const d = new Date(year, month + 1, i);
      grid.push({
        date: d,
        isCurrentMonth: false,
        dateStr: formatDate(d)
      });
    }
    return grid;
  }, [currentMonth]);
  // Handlers
  const handlePrevMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  const handleNextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  const handleAddSlot = () => {
    if (!newSlot.startTime || !newSlot.endTime) {
      showToast('Укажите время начала и окончания');
      return;
    }
    const dateStr = formatDate(selectedDate);
    const slot: Slot = {
      id: `s-${Date.now()}`,
      dateStr,
      startTime: newSlot.startTime,
      endTime: newSlot.endTime,
      type: newSlot.type,
      isBooked: false
    };
    setSlots([...slots, slot].sort((a, b) => a.startTime.localeCompare(b.startTime)));
    setShowAddSlot(false);
    showToast('Слот успешно добавлен');
  };
  const handleDeleteSlot = (id: string) => {
    setSlots(slots.filter(s => s.id !== id));
    showToast('Слот удален');
  };
  const handleClearDay = () => {
    const dateStr = formatDate(selectedDate);
    const bookedSlots = slots.filter(s => s.dateStr === dateStr && s.isBooked);
    const otherDaysSlots = slots.filter(s => s.dateStr !== dateStr);
    setSlots([...otherDaysSlots, ...bookedSlots]);
    showToast('Свободные слоты удалены');
  };
  const handleCopyDay = () => {
    showToast('Расписание скопировано. Выберите день для вставки.');
  };
  const handleApplyToWeek = () => {
    const dateStr = formatDate(selectedDate);
    const dayFreeSlots = slots.filter(s => s.dateStr === dateStr && !s.isBooked);
    if (dayFreeSlots.length === 0) {
      showToast('Нет свободных слотов для копирования');
      return;
    }
    // Get the Monday of the current week
    const dayOfWeek = selectedDate.getDay();
    const monday = new Date(selectedDate);
    monday.setDate(selectedDate.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    const newSlots: Slot[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      const dStr = formatDate(d);
      if (dStr === dateStr) continue; // skip the source day
      // Check if it's a working day
      const dayIdx = d.getDay() === 0 ? 6 : d.getDay() - 1;
      if (!settings.workingDays[dayIdx].active) continue;
      dayFreeSlots.forEach(slot => {
        newSlots.push({
          ...slot,
          id: `s-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          dateStr: dStr
        });
      });
    }
    setSlots([...slots, ...newSlots]);
    showToast(`Расписание применено к ${newSlots.length > 0 ? 'рабочим дням недели' : 'неделе'}`);
    console.log(`[Admin Sync] Schedule applied to week from ${dateStr}`);
  };
  const handleCloseDay = () => {
    const dateStr = formatDate(selectedDate);
    const remaining = slots.filter(s => !(s.dateStr === dateStr && !s.isBooked));
    setSlots(remaining);
    showToast('День закрыт — свободные слоты удалены');
    console.log(`[Admin Sync] Day closed: ${dateStr}`);
  };
  const handleSaveSettings = () => {
    showToast('Настройки расписания сохранены');
  };
  const handleApplyToAll = () => {
    showToast('Настройки применены ко всем рабочим дням');
  };
  // Derived data
  const selectedDateStr = formatDate(selectedDate);
  const daySlots = slots.filter(s => s.dateStr === selectedDateStr).sort((a, b) => a.startTime.localeCompare(b.startTime));
  const freeSlots = daySlots.filter(s => !s.isBooked);
  const dayBookings = bookings.filter(b => b.dateStr === selectedDateStr).sort((a, b) => a.startTime.localeCompare(b.startTime));
  const getTypeIcon = (type: SlotType, className: string = 'w-4 h-4') => {
    switch (type) {
      case 'video':
        return <Video className={className} data-id="element-12913" />;
      case 'audio':
        return <Phone className={className} data-id="element-12914" />;
      case 'chat':
        return <MessageCircle className={className} data-id="element-12915" />;
    }
  };
  const getTypeLabel = (type: SlotType) => {
    switch (type) {
      case 'video':
        return 'Видео';
      case 'audio':
        return 'Аудио';
      case 'chat':
        return 'Чат';
    }
  };
  const renderCalendar = () => <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-in fade-in duration-300" data-id="element-12916">
      {/* Left: Calendar Grid */}
      <div className="lg:col-span-2" data-id="element-12917">
        <Card padding="md" className="border-slate-200 h-full flex flex-col" data-id="element-12918">
          <div className="flex justify-between items-center mb-6" data-id="element-12919">
            <h3 className="text-xl font-bold text-slate-900 capitalize" data-id="element-12920">
              {MONTH_NAMES[currentMonth.getMonth()]}{' '}
              {currentMonth.getFullYear()}
            </h3>
            <div className="flex gap-2" data-id="element-12921">
              <Button variant="outline" size="sm" className="w-9 h-9 p-0" onClick={handlePrevMonth} data-id="element-12922">
                <ChevronLeft className="w-5 h-5" data-id="element-12923" />
              </Button>
              <Button variant="outline" size="sm" className="w-9 h-9 p-0" onClick={handleNextMonth} data-id="element-12924">
                <ChevronRight className="w-5 h-5" data-id="element-12925" />
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-2" data-id="element-12926">
            {DAY_NAMES.map(d => <div key={d} className="text-center text-[10px] sm:text-xs font-bold text-slate-400 uppercase tracking-wider pb-2" data-id="element-12927">
                {d}
              </div>)}
          </div>

          <div className="grid grid-cols-7 gap-1 sm:gap-2 flex-1" data-id="element-12928">
            {calendarGrid.map((cell, i) => {
            const isSelected = cell.dateStr === selectedDateStr;
            const isToday = cell.dateStr === formatDate(new Date());
            const cellSlots = slots.filter(s => s.dateStr === cell.dateStr);
            const hasBookings = cellSlots.some(s => s.isBooked);
            const hasFreeSlots = cellSlots.some(s => !s.isBooked);
            return <div key={i} onClick={() => setSelectedDate(cell.date)} className={`
                    relative aspect-square rounded-xl border p-1 sm:p-2 flex flex-col transition-all cursor-pointer
                    ${!cell.isCurrentMonth ? 'bg-slate-50/50 border-transparent opacity-40' : 'bg-white hover:border-indigo-300'}
                    ${isSelected ? 'border-indigo-500 bg-indigo-50/30 shadow-sm ring-1 ring-indigo-500' : 'border-slate-100'}
                  `} data-id="element-12929">
                  <div className="flex justify-center sm:justify-start" data-id="element-12930">
                    <span className={`
                      text-xs sm:text-sm font-semibold w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center rounded-full
                      ${isToday ? 'bg-indigo-600 text-white' : isSelected ? 'text-indigo-700' : 'text-slate-700'}
                    `} data-id="element-12931">
                      {cell.date.getDate()}
                    </span>
                  </div>

                  <div className="mt-auto flex gap-1 justify-center sm:justify-start w-full px-1 pb-1" data-id="element-12932">
                    {hasBookings && <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-emerald-500 rounded-full" title="Есть записи" data-id="element-12933"></div>}
                    {hasFreeSlots && <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-slate-300 rounded-full" title="Есть свободные слоты" data-id="element-12934"></div>}
                  </div>
                </div>;
          })}
          </div>
        </Card>
      </div>

      {/* Right: Day Details */}
      <div className="flex flex-col gap-4" data-id="element-12935">
        <Card padding="md" className="border-slate-200 flex-1 flex flex-col max-h-[calc(100vh-200px)] overflow-hidden" data-id="element-12936">
          <div className="flex justify-between items-start mb-4 border-b border-slate-100 pb-4 shrink-0" data-id="element-12937">
            <div data-id="element-12938">
              <h3 className="text-lg font-bold text-slate-900" data-id="element-12939">
                {selectedDate.getDate()} {MONTH_NAMES[selectedDate.getMonth()]}
              </h3>
              <p className="text-sm text-slate-500 capitalize" data-id="element-12940">
                {DAY_NAMES[selectedDate.getDay() === 0 ? 6 : selectedDate.getDay() - 1]}
              </p>
            </div>
            <div className="flex gap-1" data-id="element-12941">
              <Button variant="ghost" size="sm" className="w-8 h-8 p-0 text-slate-400 hover:text-indigo-600" title="Копировать расписание" onClick={handleCopyDay} data-id="element-12942">
                <Copy className="w-4 h-4" data-id="element-12943" />
              </Button>
              <Button variant="ghost" size="sm" className="w-8 h-8 p-0 text-slate-400 hover:text-indigo-600" title="Применить к неделе" onClick={handleApplyToWeek} data-id="element-12944">
                <CalendarIcon className="w-4 h-4" data-id="element-12945" />
              </Button>
              <Button variant="ghost" size="sm" className="w-8 h-8 p-0 text-slate-400 hover:text-amber-600" title="Закрыть день" onClick={handleCloseDay} data-id="element-12946">
                <AlertCircle className="w-4 h-4" data-id="element-12947" />
              </Button>
              <Button variant="ghost" size="sm" className="w-8 h-8 p-0 text-slate-400 hover:text-rose-600" title="Очистить свободные слоты" onClick={handleClearDay} data-id="element-12948">
                <Trash2 className="w-4 h-4" data-id="element-12949" />
              </Button>
            </div>
          </div>

          <div className="space-y-6 flex-1 overflow-y-auto pr-2 custom-scrollbar" data-id="element-12950">
            {/* Bookings Section */}
            {dayBookings.length > 0 && <div data-id="element-12951">
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-12952">
                  <div className="w-2 h-2 rounded-full bg-emerald-400" data-id="element-12953"></div>
                  Записи ({dayBookings.length})
                </h4>
                <div className="space-y-3" data-id="element-12954">
                  {dayBookings.map(booking => <div key={booking.id} className="p-3 rounded-xl border border-emerald-100 bg-emerald-50/50 hover:bg-emerald-50 transition-colors" data-id="element-12955">
                      <div className="flex justify-between items-start mb-2" data-id="element-12956">
                        <div className="text-sm font-bold text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded-md" data-id="element-12957">
                          {booking.startTime} - {booking.endTime}
                        </div>
                        <Badge variant="success" size="sm" data-id="element-12958">
                          Оплачено
                        </Badge>
                      </div>
                      <div className="font-bold text-slate-900 mb-0.5" data-id="element-12959">
                        {booking.clientName}
                      </div>
                      <div className="text-sm text-slate-600 mb-2" data-id="element-12960">
                        {booking.serviceName}
                      </div>

                      <div className="flex items-center justify-between mt-3" data-id="element-12961">
                        <div className="flex items-center gap-1.5 text-xs text-slate-500" data-id="element-12962">
                          {getTypeIcon(booking.type, 'w-3.5 h-3.5')}
                          {getTypeLabel(booking.type)}
                        </div>
                        <div className="flex gap-2" data-id="element-12963">
                          <Button variant="outline" size="sm" className="h-7 px-2 border-emerald-200 text-emerald-700 hover:bg-emerald-100" onClick={() => showToast('Открыт чат с клиентом')} data-id="element-12964">
                            <Mail className="w-3.5 h-3.5" data-id="element-12965" />
                          </Button>
                          <Button variant="primary" size="sm" className="h-7 px-3 bg-emerald-600 hover:bg-emerald-700 border-emerald-600 text-xs" icon={<Play className="w-3 h-3" data-id="element-12967" />} onClick={() => showToast('Консультация начата')} data-id="element-12966">
                            Начать
                          </Button>
                        </div>
                      </div>
                    </div>)}
                </div>
              </div>}

            {/* Free Slots Section */}
            <div data-id="element-12968">
              <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-12969">
                <div className="w-2 h-2 rounded-full bg-slate-300" data-id="element-12970"></div>
                Свободные слоты ({freeSlots.length})
              </h4>

              <div className="space-y-2 mb-4" data-id="element-12971">
                {freeSlots.map(slot => <div key={slot.id} className="flex items-center justify-between p-2.5 rounded-lg border border-slate-200 bg-white hover:border-indigo-200 transition-colors group" data-id="element-12972">
                    <div className="flex items-center gap-3" data-id="element-12973">
                      <div className="text-sm font-bold text-slate-700 w-24" data-id="element-12974">
                        {slot.startTime} - {slot.endTime}
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-md" data-id="element-12975">
                        {getTypeIcon(slot.type, 'w-3 h-3')}
                        {getTypeLabel(slot.type)}
                      </div>
                    </div>
                    <button onClick={() => handleDeleteSlot(slot.id)} className="p-1.5 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-md transition-colors" data-id="element-12976">
                      <Trash2 className="w-4 h-4" data-id="element-12977" />
                    </button>
                  </div>)}

                {freeSlots.length === 0 && dayBookings.length === 0 && !showAddSlot && <div className="text-center py-6 text-slate-400 text-sm border border-dashed border-slate-200 rounded-xl" data-id="element-12978">
                      День полностью свободен
                    </div>}
              </div>

              {/* Add Slot Inline Form */}
              {showAddSlot ? <div className="p-3 rounded-xl border border-indigo-200 bg-indigo-50/50 space-y-3 animate-in fade-in slide-in-from-top-2" data-id="element-12979">
                  <div className="flex justify-between items-center" data-id="element-12980">
                    <h5 className="text-sm font-bold text-slate-900" data-id="element-12981">
                      Новый слот
                    </h5>
                    <button onClick={() => setShowAddSlot(false)} className="text-slate-400 hover:text-slate-600" data-id="element-12982">
                      <X className="w-4 h-4" data-id="element-12983" />
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-2" data-id="element-12984">
                    <div data-id="element-12985">
                      <label className="block text-[10px] uppercase font-bold text-slate-500 mb-1" data-id="element-12986">
                        Начало
                      </label>
                      <input type="time" className="w-full px-2 py-1.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" value={newSlot.startTime} onChange={e => setNewSlot({
                    ...newSlot,
                    startTime: e.target.value
                  })} data-id="element-12987" />
                    </div>
                    <div data-id="element-12988">
                      <label className="block text-[10px] uppercase font-bold text-slate-500 mb-1" data-id="element-12989">
                        Окончание
                      </label>
                      <input type="time" className="w-full px-2 py-1.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" value={newSlot.endTime} onChange={e => setNewSlot({
                    ...newSlot,
                    endTime: e.target.value
                  })} data-id="element-12990" />
                    </div>
                  </div>

                  <div data-id="element-12991">
                    <label className="block text-[10px] uppercase font-bold text-slate-500 mb-1" data-id="element-12992">
                      Формат
                    </label>
                    <select className="w-full px-2 py-1.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" value={newSlot.type} onChange={e => setNewSlot({
                  ...newSlot,
                  type: e.target.value as SlotType
                })} data-id="element-12993">
                      <option value="video" data-id="element-12994">Видео-звонок</option>
                      <option value="audio" data-id="element-12995">Аудио-звонок</option>
                      <option value="chat" data-id="element-12996">Чат</option>
                    </select>
                  </div>

                  <Button variant="primary" size="sm" fullWidth onClick={handleAddSlot} data-id="element-12997">
                    Сохранить
                  </Button>
                </div> : <Button variant="outline" size="sm" fullWidth className="border-dashed border-slate-300 text-slate-600 hover:border-indigo-300 hover:text-indigo-600 hover:bg-indigo-50/50" icon={<Plus className="w-4 h-4" data-id="element-12999" />} onClick={() => setShowAddSlot(true)} data-id="element-12998">
                  Добавить слот
                </Button>}
            </div>
          </div>
        </Card>
      </div>
    </div>;
  const renderSettings = () => <div className="animate-in fade-in duration-300 max-w-3xl" data-id="element-13000">
      <Card padding="lg" className="border-slate-200" data-id="element-13001">
        <div className="flex items-center gap-3 mb-6 border-b border-slate-100 pb-4" data-id="element-13002">
          <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600" data-id="element-13003">
            <Settings className="w-5 h-5" data-id="element-13004" />
          </div>
          <div data-id="element-13005">
            <h3 className="text-xl font-bold text-slate-900" data-id="element-13006">
              Настройки расписания
            </h3>
            <p className="text-sm text-slate-500" data-id="element-13007">
              Базовые правила для вашего календаря
            </p>
          </div>
        </div>

        <div className="space-y-8" data-id="element-13008">
          {/* Working Hours */}
          <section data-id="element-13009">
            <div className="flex justify-between items-end mb-4" data-id="element-13010">
              <h4 className="text-sm font-bold text-slate-900 uppercase tracking-wider" data-id="element-13011">
                Рабочие часы
              </h4>
              <Button variant="ghost" size="sm" className="text-indigo-600 hover:bg-indigo-50" onClick={handleApplyToAll} data-id="element-13012">
                Применить ко всем
              </Button>
            </div>

            <div className="space-y-2 bg-slate-50 rounded-xl p-3 border border-slate-100" data-id="element-13013">
              {settings.workingDays.map((day, idx) => <div key={day.day} className="flex flex-col sm:flex-row sm:items-center gap-3 p-2 hover:bg-white rounded-lg transition-colors" data-id="element-13014">
                  <div className="flex items-center gap-3 min-w-[140px]" data-id="element-13015">
                    <label className="relative inline-flex items-center cursor-pointer" data-id="element-13016">
                      <input type="checkbox" className="sr-only peer" checked={day.active} onChange={e => {
                    const newDays = [...settings.workingDays];
                    newDays[idx].active = e.target.checked;
                    setSettings({
                      ...settings,
                      workingDays: newDays
                    });
                  }} data-id="element-13017" />
                      <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600" data-id="element-13018"></div>
                    </label>
                    <div className={`text-sm font-medium ${day.active ? 'text-slate-900' : 'text-slate-400'}`} data-id="element-13019">
                      {day.day}
                    </div>
                  </div>

                  {day.active ? <div className="flex items-center gap-2" data-id="element-13020">
                      <input type="time" className="border border-slate-200 rounded-lg text-sm px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white w-24" value={day.start} onChange={e => {
                  const newDays = [...settings.workingDays];
                  newDays[idx].start = e.target.value;
                  setSettings({
                    ...settings,
                    workingDays: newDays
                  });
                }} data-id="element-13021" />
                      <span className="text-slate-400" data-id="element-13022">-</span>
                      <input type="time" className="border border-slate-200 rounded-lg text-sm px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white w-24" value={day.end} onChange={e => {
                  const newDays = [...settings.workingDays];
                  newDays[idx].end = e.target.value;
                  setSettings({
                    ...settings,
                    workingDays: newDays
                  });
                }} data-id="element-13023" />
                    </div> : <Badge variant="neutral" className="bg-slate-100 text-slate-400 border-none" data-id="element-13024">
                      Выходной
                    </Badge>}
                </div>)}
            </div>
          </section>

          {/* Booking Rules */}
          <section data-id="element-13025">
            <h4 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4" data-id="element-13026">
              Правила бронирования
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-13027">
              <div className="p-4 border border-slate-200 rounded-xl bg-white" data-id="element-13028">
                <label className="block text-sm font-medium text-slate-900 mb-1" data-id="element-13029">
                  Длительность слота
                </label>
                <p className="text-xs text-slate-500 mb-3" data-id="element-13030">
                  Стандартное время консультации
                </p>
                <select className="w-full border border-slate-200 rounded-lg text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white" value={settings.slotDuration} onChange={e => setSettings({
                ...settings,
                slotDuration: e.target.value
              })} data-id="element-13031">
                  <option value="30" data-id="element-13032">30 минут</option>
                  <option value="45" data-id="element-13033">45 минут</option>
                  <option value="60" data-id="element-13034">1 час</option>
                  <option value="90" data-id="element-13035">1.5 часа</option>
                  <option value="120" data-id="element-13036">2 часа</option>
                </select>
              </div>

              <div className="p-4 border border-slate-200 rounded-xl bg-white" data-id="element-13037">
                <label className="block text-sm font-medium text-slate-900 mb-1" data-id="element-13038">
                  Буферное время
                </label>
                <p className="text-xs text-slate-500 mb-3" data-id="element-13039">
                  Перерыв между консультациями
                </p>
                <select className="w-full border border-slate-200 rounded-lg text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white" value={settings.breakDuration} onChange={e => setSettings({
                ...settings,
                breakDuration: e.target.value
              })} data-id="element-13040">
                  <option value="0" data-id="element-13041">Без перерыва</option>
                  <option value="10" data-id="element-13042">10 минут</option>
                  <option value="15" data-id="element-13043">15 минут</option>
                  <option value="30" data-id="element-13044">30 минут</option>
                </select>
              </div>

              <div className="p-4 border border-slate-200 rounded-xl bg-white sm:col-span-2" data-id="element-13045">
                <label className="block text-sm font-medium text-slate-900 mb-1" data-id="element-13046">
                  Часовой пояс
                </label>
                <p className="text-xs text-slate-500 mb-3" data-id="element-13047">
                  Ваше локальное время. Клиенты увидят слоты в своем времени.
                </p>
                <select className="w-full border border-slate-200 rounded-lg text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white" value={settings.timezone} onChange={e => setSettings({
                ...settings,
                timezone: e.target.value
              })} data-id="element-13048">
                  <option value="Europe/Moscow" data-id="element-13049">Москва (GMT+3)</option>
                  <option value="Europe/Kiev" data-id="element-13050">Киев (GMT+2)</option>
                  <option value="Asia/Almaty" data-id="element-13051">Алматы (GMT+5)</option>
                  <option value="Asia/Tbilisi" data-id="element-13052">Тбилиси (GMT+4)</option>
                </select>
              </div>
            </div>
          </section>

          <div className="pt-6 border-t border-slate-100 flex justify-end" data-id="element-13053">
            <Button variant="primary" size="lg" onClick={handleSaveSettings} icon={<CheckCircle2 className="w-5 h-5" data-id="element-13055" />} data-id="element-13054">
              Сохранить настройки
            </Button>
          </div>
        </div>
      </Card>
    </div>;
  return <div className="space-y-6" data-id="element-13056">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-2" data-id="element-13057">
        <div data-id="element-13058">
          <h2 className="text-2xl font-bold text-slate-900" data-id="element-13059">
            Календарь и расписание
          </h2>
          <p className="text-slate-500 text-sm mt-1" data-id="element-13060">
            Управление доступным временем для консультаций
          </p>
        </div>

        <div className="flex bg-slate-100 p-1 rounded-xl w-full sm:w-auto" data-id="element-13061">
          <button onClick={() => setView('calendar')} className={`flex-1 sm:flex-none flex justify-center items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${view === 'calendar' ? 'bg-white text-indigo-600 shadow-sm ring-1 ring-slate-200/50' : 'text-slate-500 hover:text-slate-900'}`} data-id="element-13062">
            <CalendarIcon className="w-4 h-4" data-id="element-13063" />
            Календарь
          </button>
          <button onClick={() => setView('settings')} className={`flex-1 sm:flex-none flex justify-center items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${view === 'settings' ? 'bg-white text-indigo-600 shadow-sm ring-1 ring-slate-200/50' : 'text-slate-500 hover:text-slate-900'}`} data-id="element-13064">
            <Settings className="w-4 h-4" data-id="element-13065" />
            Настройки
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="pt-2" data-id="element-13066">
        {view === 'calendar' ? renderCalendar() : renderSettings()}
      </div>
    </div>;
}